<template>
  <div class="collab-tiles-section">
    <div class="collab-tiles-section__header">
      <RouterLink
        class="global-h2 collab-tiles-section__title"
        :to="{
          name: 'CustomerCustomCollabs',
          params: { communityId: props.communityId },
        }"
      >
        Custom Collabs<BetaBadge class="standard-page-header__beta-badge" style="margin-left: 7px" />
      </RouterLink>
      <div class="collab-tiles-section__subtitle">Craft your own collaboration journey with creators—flexible, self-guided, and tailored exactly to your needs.</div>

      <div style="width: 100%"></div>

      <MyCustomCollabsInfoSpoiler style="margin-bottom: 5px" />

      <div style="width: 100%"></div>

      <SrpButton
        v-if="collabsToShow?.length === 0"
        class="collab-tiles-section__add-more-collabs-button"
        tag="RouterLink"
        :to="{
          name: 'CustomerCustomCollabs',
          params: { communityId: props.communityId },
          hash: '#openNewCollabModal=true',
        }"
        size="small"
        fill="outlined"
        :isDisabled="props.isCustomerDisabled"
      >
        <template #icon>
          <IconEmbedded name="plus_4" :size="18" />
        </template>
        Propose a Custom Collab
      </SrpButton>
    </div>

    <ul v-if="collabsToShow.length" class="collab-tiles-section__tiles-list">
      <CustomCollabActionSummaryTile class="collab-tiles-section__tile" v-for="collab in collabsToShow" :key="collab.customCollabId" :customCollabWidgetPayload="collab" userType="customer" />
    </ul>

    <ShowMoreBigButton v-if="props.customCollabs.length > collabsToShow.length" class="collab-tiles-section__show-all-btn" @click="showAll = true">
      View All
      <IconEmbedded name="caret-bottom_3-5" :size="18" color="rgba(120, 120, 120, 1)" style="position: relative; top: 3px; left: 5px" />
    </ShowMoreBigButton>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { useRouter } from "vue-router";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import ShowMoreBigButton from "@components/ui/ShowMoreBigButton.vue";
import CustomCollabActionSummaryTile from "@views/CustomCollabs/CustomerCustomCollabs/CustomCollabActionSummaryTile.vue";
import MyCustomCollabsInfoSpoiler from "@views/Community/Dashboard/MyCustomCollabsInfoSpoiler.vue";
import BetaBadge from "@components/BetaBadge.vue";

// Types
import SrpButton from "@components/ui/SrpButton.vue";
import { CustomCollabWidgetPayload } from "@contracts/customCollabs";

const router = useRouter();

const props = withDefaults(
  defineProps<{
    customCollabs: Array<CustomCollabWidgetPayload>;
    communityId: string;
    isCustomerDisabled: boolean;
  }>(),
  {
    customCollabs: () => [],
    communityId: "",
    isCustomerDisabled: false,
  }
);

const emit = defineEmits<{
  (e: "clickAddMoreCollabsButton"): void;
}>();

// Collabs to show ============================================================
// Only show those with activeOrRecentlyCompleted unless showAll is true
const showAll = ref(false);
const minimumToTryToShow = 4;

const collabsToShow = computed<Array<CustomCollabWidgetPayload>>(() => {
  if (showAll.value) {
    return props.customCollabs;
  } else {
    const filtered = props.customCollabs.filter(collab => collab.activeOrRecentlyCompleted);
    // Default to showing some if they just have old completed collabs
    if (filtered.length <= minimumToTryToShow) {
      return props.customCollabs.slice(0, minimumToTryToShow);
    }
    return filtered;
  }
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Collab tiles section =======================================================
.collab-tiles-section {
  &__header {
    margin-bottom: 27px;
    gap: 5px 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__title {
    text-decoration: underline;
    text-decoration-color: rgba(0, 0, 0, 0.2);
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
    text-decoration-style: solid;

    &:hover {
      text-decoration: none;
    }
  }

  &__subtitle {
    width: 100%;
  }

  &__add-more-collabs-button {
  }

  &__tiles-list {
    padding: 0;
    margin: 0 0 30px;
    gap: 27px;
    display: flex;
    flex-wrap: wrap;
    list-style: none;
  }

  &__tile {
    width: calc(50% - 15px);
  }

  &__show-all-btn {
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .collab-tiles-section {
    &__tiles-list {
      gap: 24px;
    }
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .collab-tiles-section {
    &__tiles-list {
      gap: 22px;
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .collab-tiles-section {
    &__tiles-list {
      gap: 18px;
    }

    &__tile {
      width: 100%;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .collab-tiles-section {
    &__tiles-list {
      gap: 17px;
    }

    &__tile {
      width: 100%;
    }
  }
}
</style>
